import { DateHelper } from "../helpers/date-helper";
import { CaregiverTravelling } from "./expense-check.model";
import { ExpenseImage } from "./expense-image.model";
import { UpdateEntityAmountRequest, UpdateEntityCurrencyRequest } from "./patient.model";
import { OverBudgetRequestDetailsStatus } from "./project-management.model";

export interface ExpenseClaimIdsRequest {
  expenseClaimsIds: string[];
}

export interface SendCaxtonExpenseClaimsToFinanceRequest extends ExpenseClaimIdsRequest {
}

export interface PayByCaxtonBankRequest extends ExpenseClaimIdsRequest {
  requestId: string;
}

export interface PayByCaxtonCardRequest extends PayByCaxtonBankRequest { }

export interface PayByNmibleCardRequest extends PayByCaxtonBankRequest { }

export interface PayByNmibleBankRequest extends PayByCaxtonBankRequest { }

export interface UnloadFromCaxtonCardRequest extends PayByCaxtonBankRequest { }

export interface NotOnCaxtonRetryRequest extends ExpenseClaimIdsRequest {
  findings: string;
}

export interface MoveToAutomatedPaymentFailedRequest extends ExpenseClaimIdsRequest { }

export interface CheckExpenseStatusOnNmibleRequest {
  nmibleTransactionId: string;
}

export interface CheckExpenseStatusOnNmibleResult {
  outcome: 'Paid' | 'Failed' | 'StillProcessing';
  message: string;
}

export enum NmibleManualPaymentStatus {
  Queued = 0,
  InProgress = 1,
  Success = 2,
  Failure = 3,
}

export interface UpdateNmiblePaymentStatusManuallyRequest {
  nmibleTransactionId: string;
  status: NmibleManualPaymentStatus;
}

export interface SendToFinanceRequest extends ExpenseClaimIdsRequest {
  reason: string;
}

export interface SetCaxtonTransferIdRequest {
  expenseClaimIds: string[];
  transferId: number;
  findings: string;
  expenseClaimPaymentMethod: ExpenseClaimPaymentMethod;
}

export interface AddExpenseClaimNoteRequest {
  expenseClaimId: string;
  note: string;
}

export interface StartExpenseClaimInvestigationRequest {
  expenseClaimId: string;
  reasonForInvestigation: string;
  allClaims: boolean;
}

export interface CloseExpenseClaimInvestigationRequest {
  expenseClaimId: string;
  allClaims: boolean;
}

export enum ExpenseClaimPaidBy {
  Finance,
  Caxton,
  CaxtonCard,
  NmibleCard,
  NmibleBank
}

export interface ExpenseApprovalSuggestionResult {
  autoApprovalResult: AutoApprovalResult;
  claimApprovalResult?: ClaimApprovalResult;
  approvalCheckResults?: ApprovalCheckResult[];
}

export class ReasonForEdit {
  public id: string;
  public expenseId: string;
  public reason: string;
  public created: Date;

  public static fromObject(obj: any): ReasonForEdit {
    let reason = new ReasonForEdit();
    reason.id = obj.id;
    reason.expenseId = obj.expenseId;
    reason.reason = obj.reason;
    reason.created = DateHelper.dateUTCToLocal(obj.created);

    return reason;
  }
}

export class PatientPaymentMethod {
  public static Methods = {
    0: "Bank Transfer",
    1: "Caxton Card"
  }
}

export class ExpensePatientGroup {
  public currencyGroupExpenses: ExpenseCurrencyGroup[];
}

export class ExpenseCurrencyGroup {
  public patientId: string;
  public patientName: string;
  public patientTrialId: string;
  public patientCode: string;
  public patientAddress: string;
  public patientCountry: string;
  public totalExpense: number;
  public paymentDate: Date;
  public currency: string;
  public bankFeeCover: number;
  public bankFeeCoverCurrency: string;
  public paymentMethod: ExpenseClaimPaymentMethod;
  public displayBankFeeCover: boolean;
  public trialCode: string;
  public trialNickname: string;
  public trialOpportunityNumber: string;
  public trialOracleCode: string;
  public trialId: string;
  public nmibleTransactionId: string;
  public isPreloadRedemptionGroup: boolean;
  public expenses: ExpenseClaimViewModel[]
}

export interface ApproveExpenseClaimResult {
  approved: boolean;
  state?: ExpenseClaimState;
  caxtonIncompatibleMessage: string;
}

export interface CaxtonIncompatibleData {
  caxtonIncompatibleReason: string;
  expenseClaimId: string;
  type: ExpenseCategoryType;
}

export enum ExpenseClaimState {
  Pending = 0,
  Approved = 1,
  Paid = 2,
  Rejected = 3,
  AutomatedPaymentApproved = 4,
  Processing = 5,
  AutomatedPaymentFailed = 6
}

export enum ExpenseClaimPaymentMethod {
  ManualBankTransfer = 0,
  CaxtonCard = 1,
  CaxtonAutomatedBankTransfer = 2,
  NmibleCard = 3,
  NmibleAutomatedBankTransfer = 4,
}

export function isNmiblePaymentMethod(paymentMethod: ExpenseClaimPaymentMethod): boolean {
  return paymentMethod === ExpenseClaimPaymentMethod.NmibleCard || paymentMethod === ExpenseClaimPaymentMethod.NmibleAutomatedBankTransfer;
}

export class UpdateExpenseClaimRequest {
  trialId: string;
  visitId: string;
  expenseCategoryId: string;
  // This should be enum - Adis
  subCategory: number;
  currency: string;
  amount?: number;
  amountBC?: number;
  recalculateAmountBc?: boolean;
  distanceUnit: string;
  distanceAmount?: number;
  rejectionReason: string;
  reasonForEdit: string;
  paymentMethod: ExpenseClaimPaymentMethod;
  onHold: boolean;
  overspendApproved: boolean;
  notes: string;
  claimFormId: string;
  claimFormUrl: string;
  claimFormDate?: string;
  fullyReconciled: boolean;
}

export enum AutoApprovalResult {
  Approve,
  Reject,
  ManualReview,
  Pending
}

export class AutoApprovalResultString {
  static Values: { [key in AutoApprovalResult]: string } = {
    [AutoApprovalResult.Approve]: "Approve",
    [AutoApprovalResult.Reject]: "Reject",
    [AutoApprovalResult.ManualReview]: "Manual Review",
    [AutoApprovalResult.Pending]: "Pending",
  };
}

export interface ExpenseSuggestionData {
  icon: string;
  text: string;
}

export enum ExpenseCategoryType {
  Value,
  Distance
}

export enum OrderBy {
  Oldest,
  Newest
}

export class OrderByString {
  static Values: { [key in OrderBy]: string } = {
    [OrderBy.Oldest]: "Oldest",
    [OrderBy.Newest]: "Newest",
  };
}

export interface GetExpenseClaimsRequest {
  page: number;
  orderBy: OrderBy;
  state: ExpenseClaimState;
  onHold: boolean;
  investigations: boolean;
  preloads: boolean;
  keywords?: string;
  trialId?: string;
  patientId?: string;
  wipActionedById?: string;
}

export class ExpenseClaimReceiptModel {
  public filename: string;
  public amount: number;
  public categoryName: string;
  public categoryConfidence: number;
  public transactionDate: Date;
  public currency: string;
  public legitimacyConfidence: number;
  public merchantName: string;

  constructor(init?: Partial<ExpenseClaimReceiptModel>) {
    Object.assign(this, init);

    if (init && init.amount)
      this.amount = Math.round(init.amount * 100) / 100;

    if (init && init.transactionDate)
      this.transactionDate = new Date(init.transactionDate);
  }
}

export interface ApprovalCheckResult {
  checkName: string;
  passed: boolean;
  reason: string;
  isAiCheck: boolean;
}

export class ClaimApprovalResult {
  public approve: boolean;
  public reasons: string[] = [];

  constructor(init?: Partial<ClaimApprovalResult>) {
    Object.assign(this, init);
  }
}

export class ExpenseClaimViewModel {
  id: string;
  state: ExpenseClaimState;
  visitTitle: string;
  categoryName: string;
  categoryType: ExpenseCategoryType;
  amount: number;
  distanceAmount: number;
  currency: string;
  distanceUnit: string;
  rejectionReason: string;
  notes: string;
  imageUrls: ExpenseImage[];
  created: Date;
  dateApproved?: Date;
  dateRejected?: Date;
  datePaid?: Date;
  underInvestigation: boolean;
  reasonForInvestigation: string;
  subCategory: ExpenseSubCategory;
  patientId: string;
  patientCode: string;
  patientName: string;
  patientAddress: string;
  patientCountry: string;
  patientTrialId: string;
  trialCode: string;
  trialNickname: string;
  trialOpportunityNumber: string;
  trialOracleCode: string;
  trialId: string;
  paymentMethod: ExpenseClaimPaymentMethod;
  addedByPatient: boolean;
  paymentRequestedOn: Date;
  expectedClearanceDate: Date;
  paidBy: ExpenseClaimPaidBy;
  cardIdentifier: string;
  caxtonTransferId: string;
  sendToFinanceReason: string;
  visitId: string;
  visitType?: VisitType;
  visitAttendance?: VisitAttendance;
  visitDate?: Date;
  visitEndDate?: Date;
  visitTravelDays?: number;
  currencySymbol: string;
  policyReminders: string;
  type: string;
  caxtonFailedReason: string;
  caxtonFailedFindings: string;
  trialExpensePolicyReminders: string;
  adminExpenseNotes: string;
  bankFeesIncurred: boolean;
  overspendMileage: boolean;
  overspendTravel: boolean;
  approvalNoReceipts: boolean;
  fixedFeePerVisit: boolean;
  overspendApproved: boolean;
  overBudgetRequestRaised: boolean;
  overBudgetRequestStatus?: OverBudgetRequestDetailsStatus;
  approvalSuggestion?: AutoApprovalResult;
  approvalCheckResults: ApprovalCheckResult[] = [];
  referredBack: boolean;
  reasonsForEdit: ReasonForEditViewModel[];
  fullyReconciled: boolean;
  isWip: boolean;
  wipDate: Date;
  wipActionedByFirstname: string;
  wipActionedByLastname: string;
  receipts: ExpenseClaimReceiptModel[] = [];
  claimApprovalResult: ClaimApprovalResult = new ClaimApprovalResult();
  dailySpendExpensesEnabled: boolean;

  public static fromObject(obj: any): ExpenseClaimViewModel {
    let claim = new ExpenseClaimViewModel();

    claim.id = obj.id;
    claim.state = obj.state;
    claim.visitTitle = obj.visitTitle;
    claim.categoryName = obj.categoryName;
    claim.categoryType = obj.categoryType;
    claim.amount = obj.amount;
    claim.distanceAmount = obj.distanceAmount;
    claim.currency = obj.currency;
    claim.distanceUnit = obj.distanceUnit;
    claim.rejectionReason = obj.rejectionReason;
    claim.notes = obj.notes;
    claim.adminExpenseNotes = obj.adminExpenseNotes;
    claim.imageUrls = obj.imageUrls;
    claim.created = obj.created ? DateHelper.dateUTCToLocal(obj.created) : null;
    claim.dateApproved = obj.dateApproved ? DateHelper.dateUTCToLocal(obj.dateApproved) : null;
    claim.dateRejected = obj.dateRejected ? DateHelper.dateUTCToLocal(obj.dateRejected) : null;
    claim.datePaid = obj.datePaid ? DateHelper.dateUTCToLocal(obj.datePaid) : null;
    claim.underInvestigation = obj.underInvestigation;
    claim.reasonForInvestigation = obj.reasonForInvestigation;
    claim.subCategory = obj.subCategory;
    claim.patientId = obj.patientId;
    claim.patientCode = obj.patientCode;
    claim.patientName = obj.patientName;
    claim.patientAddress = obj.patientAddress;
    claim.patientCountry = obj.patientCountry;
    claim.patientTrialId = obj.patientTrialId;
    claim.trialCode = obj.trialCode;
    claim.trialNickname = obj.trialNickname;
    claim.trialOpportunityNumber = obj.trialOpportunityNumber;
    claim.trialOracleCode = obj.trialOracleCode;
    claim.trialId = obj.trialId;
    claim.paymentMethod = obj.paymentMethod;
    claim.addedByPatient = obj.addedByPatient;
    claim.paymentRequestedOn = obj.paymentRequestedOn ? new Date(obj.paymentRequestedOn) : null;
    claim.expectedClearanceDate = obj.expectedClearanceDate ? new Date(obj.expectedClearanceDate) : null;
    claim.paidBy = obj.paidBy;
    claim.cardIdentifier = obj.cardIdentifier;
    claim.caxtonTransferId = obj.caxtonTransferId;
    claim.sendToFinanceReason = obj.sendToFinanceReason;
    claim.visitId = obj.visitId;
    claim.visitType = obj.visitType;
    claim.visitAttendance = obj.visitAttendance;
    claim.visitDate = obj.visitDate ? DateHelper.toDateNoTimeZone(obj.visitDate) : null;
    claim.visitEndDate = obj.visitEndDate ? DateHelper.toDateNoTimeZone(obj.visitEndDate) : null;
    claim.visitTravelDays = obj.visitTravelDays;
    claim.currencySymbol = obj.currencySymbol;
    claim.policyReminders = obj.policyReminders;
    claim.type = obj.type;
    claim.caxtonFailedReason = obj.caxtonFailedReason;
    claim.caxtonFailedFindings = obj.caxtonFailedFindings;
    claim.trialExpensePolicyReminders = obj.trialExpensePolicyReminders;
    claim.adminExpenseNotes = obj.adminExpenseNotes;
    claim.bankFeesIncurred = obj.bankFeesIncurred;
    claim.overspendMileage = obj.overspendMileage;
    claim.overspendTravel = obj.overspendTravel;
    claim.approvalNoReceipts = obj.approvalNoReceipts;
    claim.fixedFeePerVisit = obj.fixedFeePerVisit;
    claim.overspendApproved = obj.overspendApproved;
    claim.overBudgetRequestRaised = obj.overBudgetRequestRaised;
    claim.overBudgetRequestStatus = obj.overBudgetRequestStatus;
    claim.approvalSuggestion = obj.approvalSuggestion;
    claim.approvalCheckResults = obj.approvalCheckResults ? JSON.parse(obj.approvalCheckResults) : [];
    claim.reasonsForEdit = obj.reasonsForEdit;
    claim.referredBack = obj.referredBack;
    claim.fullyReconciled = obj.fullyReconciled;
    claim.isWip = obj.isWip;
    claim.wipDate = obj.wipDate ? new Date(obj.wipDate) : null;
    claim.wipActionedByFirstname = obj.wipActionedByFirstname;
    claim.wipActionedByLastname = obj.wipActionedByLastname;
    claim.receipts = obj.receipts.map(r => new ExpenseClaimReceiptModel(r));
    claim.claimApprovalResult = new ClaimApprovalResult(obj.claimApprovalResult);
    claim.dailySpendExpensesEnabled = obj.trialDailySpendExpensesEnabled;

    return claim;
  }

}

export interface ReasonForEditViewModel {
  reason: string;
  created: Date;
}

// Must stay in lockstep with PatientGo.Domain.Enums.ExpenseSubCategory — the CMS sends the
// numeric value straight back to the API. ExpenseSubCategoryEnumParityTests enforces this.
export enum ExpenseSubCategory {
  None = 0,
  CardPreloadPayment = 1,
  CardPreloadRedemption = 2,
  StipendPayment = 3,
  BankFees = 4,
  FixedFee = 5,
  Ediary = 6,
  Tax = 7
}

export enum VisitType {
  OnSite = 0,
  Home = 1,
  Virtual = 2,
  Telephone = 3
}

export enum ExpenseClaimType {
  Currency,
  Distance
}

export enum VisitAttendance {
  Cancelled = 0,
  Attended = 1,
  NoShow = 2,
  PendingConfirmation = 3,
  AutoAttended = 4
}

export interface OverBudgetRequestDetailsViewModel {
  ruleName: string;
  categories: string[];
  expenseClaims: ExpenseClaimOverBudgetRequestDetails[],
  budgetDetails: OverBudgetRequestBudgetDetails[],
  budgetCost: number,
  actualBudgetCostCurrency: string,
  actualBudgetCost?: number;
  careGiver?: CaregiverTravelling,
  blanketApprovalForNoReceipts: boolean,
  overBudgetRequirement: string
}

export interface ExpenseClaimOverBudgetRequestDetails {
  created: Date,
  amount?: number,
  currency: string,
  category: string,
  state: ExpenseClaimState,
  receiptUrls: ExpenseImage[],
  receiptMissing: boolean
}

export interface OverBudgetRequestBudgetDetails {
  actualCost: number,
  localCurrency: string,
}

export interface CreateOverBudgetRequestByExpenseClaimRequest {
  expenseClaimId: string;
  requiredInformation: string;
  actualCostInBudgetCurrency?: number;
}

export interface CreateOverBudgetRequestByCategoriesRequest {
  requiredInformation: string;
  actualCostInBudgetCurrency?: number;
  budgetCost?: number;
  categories: string[];
  visitId: string;
}

export interface ExpenseCategorySelected {
  checked: boolean;
  category: string;
}

export interface UpdateExpenseClaimResult {
  saved: boolean;
}

export interface UpdateExpenseClaimAmountRequest extends UpdateEntityAmountRequest {
}

export interface UpdateExpenseClaimAmountBcRequest extends UpdateEntityAmountRequest {
}

export interface UpdateExpenseClaimCurrencyRequest extends UpdateEntityCurrencyRequest {
}

export interface AppendFileToExpenseClaimRequest {
  expenseClaimId: string;
  fileName: string;
}

export interface RemoveFileFromExpenseClaimRequest extends AppendFileToExpenseClaimRequest { }

export interface UploadResponse {
  success: boolean;
  fileName: string;
}


export interface SebelaImportValidationResponse {
  reference: string;
  success: boolean;
  message: string;
  numberOfSingleCyclePatientUpdates: number;
  numberOfMultiCyclePatientUpdates: number;
}

export enum SebelaImportValidationResult {
  Pending = 0,
  Failed = 1,
  Verified = 2
}
